import React from "react";
import styled from "@emotion/styled";
import { Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { Coordinates } from "@optimusride/client-library/@types/grpc/common";
import { FONT_0_625, FONT_0_875 } from "../../common/styles/fonts";
import {
    BLACK, ORANGE_500, ORANGE_600, WHITE,
} from "../../common/styles/colors";
import {
    SPACING_4, SPACING_1, SPACING_8, SPACING_24, SPACING_16, SPACING_28,
} from "../../common/styles/spacing";
import { H1 } from "../../common/styles/typography";
import LocationIcon from "../assets/LocationIcon";
import { getAddressString, Address } from "../utils";

const StyledStationHeading = styled(H1)`
    font-size: ${FONT_0_875};
    font-weight: bold;

    margin: ${SPACING_4} 0;

    letter-spacing: ${SPACING_1};
`;

type StyledButtonProps = {
    bgcolor?: string;
    hovercolor?: string;
};

const StyledButton = styled(Button)`
    background-color: ${({ bgcolor = BLACK }: StyledButtonProps) => bgcolor};
    color: ${WHITE};
    border-radius: ${SPACING_8};
    align-self: center;

    margin-right: ${SPACING_16};
    padding-right: ${SPACING_24};
    padding-left: ${SPACING_24};
    
    &:first-of-type {
        margin-left: auto;
    }

    &:hover {
        background-color: ${({ hovercolor = BLACK }: StyledButtonProps) => hovercolor};
    }
`;

const StyledStationBody = styled.div`
    display: flex;
    flex-direction: column;
`;

const StyledLocationIcon = styled(LocationIcon)`
    width: ${SPACING_28};
    margin-right: ${SPACING_16};
`;

const StyledAddress = styled.address`
    font-size: ${FONT_0_625};
`;

const AddressComponent = ({ address }: { address?: Address }) => {
    const addressString = getAddressString(address);

    return (
        <StyledAddress>
            {addressString
                .split("\n")
                .map((addressValue: string) => (
                    <React.Fragment key={addressValue}>
                        {addressValue}
                        <br />
                    </React.Fragment>
                ))}
        </StyledAddress>
    );
};

export default ({
    nickname,
    letterID,
    address,
    latLon,
    disabled,
    isAdmin = false,
    handleClick,
    handleStationEdit,
}: {
    nickname: string;
    letterID: string;
    address?: Address;
    latLon?: Coordinates;
    disabled?: boolean;
    isAdmin?: boolean;
    handleClick: () => void;
    handleStationEdit: () => void;
}) => {
    const { t } = useTranslation();

    return (
        <>
            <StyledLocationIcon text={letterID} disabled={disabled} />
            <StyledStationBody>
                <StyledStationHeading>
                    {nickname}
                </StyledStationHeading>
                <AddressComponent address={address} />
            </StyledStationBody>
            {isAdmin && (
                <StyledButton
                    bgcolor={ORANGE_500}
                    hovercolor={ORANGE_600}
                    onClick={handleStationEdit}
                >
                    {t("home.station.edit")}
                </StyledButton>
            )}
            {latLon && !disabled && <StyledButton onClick={handleClick}>{t("home.station.view")}</StyledButton>}
        </>
    );
};
